'use client';

import type { MonolithPayInValidationResponse, MonolithPaymentsPlayerDataGetResponse } from '@payments/aurum-contracts';
import { API_ROUTES, useApiMutation, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Button, FormField } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { useSeonSession } from '@shared/hooks';
import { AppStore, Spinner } from '@shared/icons';
import { NumberFormats, ROUTES, useClientConfig, useCurrencyFormatter } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Checkbox } from '../../../../../form-controls/Checkbox/Checkbox';
import { FieldMessage } from '../../../../../form-controls/FieldMessage/FieldMessage';
import { useMultistepForm } from '../../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { TextInput } from '../../../../../form-controls/TextInput/TextInput';
import { Link } from '../../../../../Link/Link';
import type { DepositLimit } from '../../../../../my-account/SaferGambling/types';
import { getDepositLimitsPath, getPendingDepositLimitPath, isEncodedPendingLimitRemoval } from '../../../../../my-account/SaferGambling/utils/playerLimits';
import { DEPOSIT_PAYMENT_METHODS } from '../../../../../PaymentMethodSelector/PaymentMethods';
import { PaymentMethodSelector } from '../../../../../PaymentMethodSelector/PaymentMethodSelector';
import type { MethodOrInstrument } from '../../../../../PaymentMethodSelector/types';
import { StatusNotification } from '../../../../../StatusNotification/StatusNotification';
import type { StatusNotificationAction } from '../../../../../StatusNotification/types';
import { toast } from '../../../../../Toast/Toast';
import { useGlobalSheet } from '../../../../context/GlobalSheetContext';
import { APPLE_PAY_BUTTON_CLASS_NAME, APPLE_PAY_ICON_CLASS_NAME } from '../../../applePayButtonClassName';
import { useDirectDepositTransaction } from '../../../hooks/useDirectDepositTransaction';
import { useApplePay } from '../../hooks/useApplePay';
import type { DepositAmountStepProps, DepositTransactionPayload, FormData } from '../../types';

// Extend Window interface for test helpers
declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __TEST_APPLE_PAY_PAYMENT_AUTHORIZED__?: (paymentData: {
      data: string;
      signature: string;
      transactionId: string;
      ephemeralPublicKey: string;
      publicKeyHash: string;
      version: string;
    }) => Promise<boolean>;
    __TEST_APPLE_PAY_ERROR__?: (errorMessage: string) => void;
  }
}

function findPendingRaiseLimit(limits: DepositLimit[]): DepositLimit | null {
  return limits.find(limit => limit.limitType === 'DEPOSIT' && limit.pendingLimit && !isEncodedPendingLimitRemoval(limit.pendingLimit.value)) ?? null;
}

function DepositAmountStep({
  namespace,
  onError,
  onTransactionStart,
  depositLimits: depositLimitsProp,
  isDepositLimitsLoading: isDepositLimitsLoadingProp,
  depositFlowInstance,
  setTrackingDetails,
  minimumAmountForBonus,
  autoProcessPaymentMethod,
  ...props
}: DepositAmountStepProps) {
  const debug = createDebug({ isEnabled: false });
  const t = useTranslations(namespace);
  const tGeneral = useTranslations('general');
  const queryClient = useQueryClient();
  const [isAutoProcessPending, setIsAutoProcessPending] = useState(!!autoProcessPaymentMethod);
  const autoProcessCalledRef = useRef(false);
  const previousStatusRef = useRef<string | undefined>(undefined);
  const {
    watch,
    setValue,
    setError,
    formState: { errors },
    trigger,
  } = useFormContext<FormData>();
  const { goToNextStep, setIsLoading, isLoading, skipToStep, setStatus, status } = useMultistepForm();
  const { showDepositSheetHeader, resetDepositSheetTitle } = useGlobalSheet();
  const { session } = useSession();
  const playerId = session?.profile?.id;
  const idempotencyToken = `${playerId}-${Date.now()}`;
  const [pendingDepositLimit, setPendingDepositLimit] = useState<DepositLimit | null>(null);
  const [pendingLimitActions, setPendingLimitActions] = useState<StatusNotificationAction[]>([]);
  const formatter = useCurrencyFormatter();
  const { currency } = useClientConfig();
  const nf = NumberFormats;

  const amount = watch('amountStep.amount');
  const paymentMethod = watch('amountStep.paymentMethod');
  const termsAccepted = watch('amountStep.termsAccepted');
  const [selectedPaymentMethodOrInstrument, setSelectedPaymentMethodOrInstrument] = useState<MethodOrInstrument | null>(null);
  const selectedPaymentMethodOrInstrumentRef = useRef<MethodOrInstrument | null>(null);

  const {
    PAYMENTS_FLAGS: { showDepositLimitButtonOnDepositAmountStep, requiresFirstTimeDepositTermsAcceptance },
    APPLE_PAY_CONFIG: { playerCountry, merchantLabel },
    DEPOSIT_AMOUNTS: { quickAmounts },
  } = getSiteConfig();

  useEffect(() => {
    if (paymentMethod && !selectedPaymentMethodOrInstrument) {
      const matchingMethod = Object.values(DEPOSIT_PAYMENT_METHODS).find(method => method.name === paymentMethod);
      if (matchingMethod) {
        setSelectedPaymentMethodOrInstrument(matchingMethod);
        selectedPaymentMethodOrInstrumentRef.current = matchingMethod;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  useEffect(() => {
    selectedPaymentMethodOrInstrumentRef.current = selectedPaymentMethodOrInstrument;
  }, [selectedPaymentMethodOrInstrument]);

  const handleDirectDepositError = useCallback(
    (message: string) => {
      setIsLoading(false);
      onError(message, 'PROCESSING_DEPOSIT');
    },
    [setIsLoading, onError]
  );

  const handleBeforeDirectDeposit = useCallback(() => {
    setIsLoading(true);
    onTransactionStart();
  }, [setIsLoading, onTransactionStart]);

  const { getSeonSessionId } = useSeonSession();

  const { processDirectDeposit } = useDirectDepositTransaction({
    amount: amount || '0',
    depositFlowInstance,
    onError: handleDirectDepositError,
    onBeforeTransaction: handleBeforeDirectDeposit,
  });

  const showMinimumAmountNotMetWarning = !!minimumAmountForBonus && !!amount && Math.round(Number(amount) * 100) < minimumAmountForBonus;

  const { data: paymentsLimits, isLoading: isPaymentsLimitsLoading } = useApiQuery<MonolithPayInValidationResponse>(API_ROUTES.PAYMENTS.DEPOSIT_LIMITS, {
    credentials: true,
  });

  const { data: paymentsPlayersData, isFetched: isPaymentsPlayersFetched } = useApiQuery<MonolithPaymentsPlayerDataGetResponse>(API_ROUTES.PAYMENTS.PAYMENTS_PLAYERS, {
    credentials: true,
  });
  const showTermsCheckbox = requiresFirstTimeDepositTermsAcceptance && isPaymentsPlayersFetched && (paymentsPlayersData?.hasToAcceptTermsAndConditions ?? false);

  useEffect(() => {
    // NOTE: If a market does not require terms acceptance, we set it *locally* to true to allow the player to proceed.
    if (!requiresFirstTimeDepositTermsAcceptance) {
      setValue('amountStep.termsAccepted', true);
      return;
    }

    if (isPaymentsPlayersFetched && !paymentsPlayersData?.hasToAcceptTermsAndConditions) {
      setValue('amountStep.termsAccepted', true);
    }
  }, [requiresFirstTimeDepositTermsAcceptance, paymentsPlayersData?.hasToAcceptTermsAndConditions, isPaymentsPlayersFetched, setValue]);

  const netDepositLimit = paymentsLimits?.limits?.find(limit => limit.limitType === 'NET_DEPOSIT');
  const amountExceedsNetDepositLimit =
    !!netDepositLimit && Number(netDepositLimit.remainingAmount || 0) > 0 && Math.round(Number(amount || '0') * 100) > Number(netDepositLimit.remainingAmount);

  const getTranslationKeyForPeriod = (period: 'DAY' | 'WEEK' | 'MONTH') => {
    switch (period) {
      case 'DAY':
        return 'daily';
      case 'WEEK':
        return 'weekly';
      case 'MONTH':
        return 'monthly';
    }
  };

  const createMutationOptions = () => ({
    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['api', getDepositLimitsPath()] });
      },
      onError: (error: Error) => {
        debug.forceError('Error updating deposit limit:', error);
      },
    },
    requestOptions: {
      credentials: true,
    },
  });

  const acceptLimit = {
    DAY: useApiMutation<void, void>(getPendingDepositLimitPath('DAY'), 'POST', createMutationOptions()),
    WEEK: useApiMutation<void, void>(getPendingDepositLimitPath('WEEK'), 'POST', createMutationOptions()),
    MONTH: useApiMutation<void, void>(getPendingDepositLimitPath('MONTH'), 'POST', createMutationOptions()),
  };

  const declineLimit = {
    DAY: useApiMutation<void, void>(getPendingDepositLimitPath('DAY'), 'DELETE', createMutationOptions()),
    WEEK: useApiMutation<void, void>(getPendingDepositLimitPath('WEEK'), 'DELETE', createMutationOptions()),
    MONTH: useApiMutation<void, void>(getPendingDepositLimitPath('MONTH'), 'DELETE', createMutationOptions()),
  };

  useEffect(() => {
    showDepositSheetHeader();
    resetDepositSheetTitle();
  }, [showDepositSheetHeader, resetDepositSheetTitle]);

  // Use deposit limits from props if provided, otherwise fetch them
  const { data: depositLimitsQueryData, isLoading: isDepositLimitsLoadingQuery } = useApiQuery<MonolithPayInValidationResponse>(API_ROUTES.PAYMENTS.DEPOSIT_LIMITS, {
    credentials: true,
    queryOptions: {
      enabled: !depositLimitsProp,
    },
  });

  const depositLimits = depositLimitsProp ?? depositLimitsQueryData ?? null;
  const isDepositLimitsLoading = isDepositLimitsLoadingProp ?? isDepositLimitsLoadingQuery;
  const { createDepositTransactionMutation, compoundId } = depositFlowInstance;

  const handleApplePayPaymentAuthorized = async (paymentData: {
    data: string;
    signature: string;
    transactionId: string;
    ephemeralPublicKey: string;
    publicKeyHash: string;
    version: string;
  }) => {
    setApplePayData(paymentData);
    return await handleApplePayTransaction();
  };

  const {
    isLoading: isApplePayLoading,
    handleClick: handleApplePayClick,
    getData: getApplePayData,
    setData: setApplePayData,
  } = useApplePay({
    amount: amount || '0',
    currency,
    playerCountry,
    merchantLabel,
    onError: (errorMessage: string) => {
      debug.forceError('Apple Pay error:', errorMessage);
      onError(errorMessage, 'PROCESSING_DEPOSIT');
    },
    onPaymentAuthorized: handleApplePayPaymentAuthorized,
  });

  useEffect(() => {
    if (compoundId) {
      const methodName = selectedPaymentMethodOrInstrument?.name || paymentMethod;
      setTrackingDetails({
        transactionId: compoundId,
        method: methodName,
        amount,
        provider: methodName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compoundId, selectedPaymentMethodOrInstrument]);

  const validateAgainstDepositLimits = () => {
    if (!depositLimits?.limits) {
      return true;
    }
    return depositLimits.limits
      .filter(limit => limit.limitType === 'DEPOSIT')
      .every(limit => {
        const remainingAmount = ('remaining' in limit ? limit.remaining : undefined) ?? ('remainingAmount' in limit ? limit.remainingAmount : undefined);
        // If limit doesn't have remainingAmount property or it's undefined/null, there's no limit set - skip validation
        if (remainingAmount === undefined || remainingAmount === null) {
          return true;
        }
        // Only validate limits that have a remainingAmount value set
        return (remainingAmount ?? 0) >= Math.round(Number(amount) * 100);
      });
  };

  const validateAgainstPaymentMethodLimits = (): { isValid: boolean; errorMessage?: string } => {
    if (!amount) {
      return { isValid: true };
    }

    // Use ref to get the latest value immediately, even if state hasn't updated yet
    const methodOrInstrument =
      selectedPaymentMethodOrInstrumentRef.current ||
      selectedPaymentMethodOrInstrument ||
      (paymentMethod ? Object.values(DEPOSIT_PAYMENT_METHODS).find(method => method.name === paymentMethod) : null);

    if (!methodOrInstrument) {
      return { isValid: true };
    }

    const amountInPence = Math.round(Number(amount) * 100);
    const { minAllowedAmount, maxAllowedAmount } = methodOrInstrument;

    if (minAllowedAmount !== undefined && amountInPence < minAllowedAmount) {
      const formattedMinAmount = formatter(minAllowedAmount).format(nf.WITH_DECIMALS).get();
      return {
        isValid: false,
        errorMessage: t('amount.min_amount_error', { amount: formattedMinAmount }),
      };
    }

    if (maxAllowedAmount !== undefined && amountInPence > maxAllowedAmount) {
      const formattedMaxAmount = formatter(maxAllowedAmount).format(nf.WITH_DECIMALS).get();
      return {
        isValid: false,
        errorMessage: t('amount.max_amount_error', { amount: formattedMaxAmount }),
      };
    }

    return { isValid: true };
  };

  const dispatchPaymentMethod = useCallback(
    async (method: MethodOrInstrument) => {
      if (method.name === DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name) {
        await handleApplePayClick();
        return;
      }

      if (method.name === DEPOSIT_PAYMENT_METHODS.CARD.name) {
        goToNextStep();
        return;
      }

      await processDirectDeposit(method);
    },
    [handleApplePayClick, goToNextStep, processDirectDeposit]
  );

  const handleConfirm = async () => {
    const isValid = await trigger('amountStep');
    if (!isValid) return;

    const paymentMethodValidation = validateAgainstPaymentMethodLimits();
    if (!paymentMethodValidation.isValid) {
      setError('amountStep.amount', {
        type: 'manual',
        message: paymentMethodValidation.errorMessage || '',
      });
      return;
    }

    const isValidAgainstDepositLimits = validateAgainstDepositLimits();
    if (!isValidAgainstDepositLimits) {
      setError('amountStep.amount', {
        type: 'manual',
        message: t('amount.deposit_limit_exceeded'),
      });
      return;
    }

    setValue('amountStep.amount', amount);
    setValue('amountStep.paymentMethod', paymentMethod);
    setValue('amountStep.termsAccepted', termsAccepted);

    if (!selectedPaymentMethodOrInstrument) {
      return;
    }

    await dispatchPaymentMethod(selectedPaymentMethodOrInstrument);
  };

  const handleApplePayTransaction = async (): Promise<boolean> => {
    setIsLoading(true);
    onTransactionStart();

    try {
      const encodedPaymentData = await getApplePayData();
      const seonSessionId = await getSeonSessionId();

      const payload: DepositTransactionPayload = {
        idempotencyToken,
        amount: Math.round(Number(amount) * 100),
        paymentMethod: DEPOSIT_PAYMENT_METHODS.APPLE_PAY.apiName,
        acceptedTermsAndConditions: true,
        paymentMethodData: encodedPaymentData,
        ...(seonSessionId && { seonSessionId }),
      };

      await createDepositTransactionMutation.mutateAsync(payload);
      debug.log('Apple Pay transaction successful');
      return true;
    } catch (error) {
      debug.error('Error creating deposit transaction:', error);
      setIsLoading(false);
      onError(t('aurumErrors.unknown'), 'PROCESSING_DEPOSIT');
      return false;
    }
  };

  const processPaymentDirectly = useCallback(
    async (method: MethodOrInstrument) => {
      selectedPaymentMethodOrInstrumentRef.current = method;
      setSelectedPaymentMethodOrInstrument(method);
      setIsAutoProcessPending(false);
      await dispatchPaymentMethod(method);
    },
    [dispatchPaymentMethod]
  );

  useEffect(() => {
    if (!autoProcessPaymentMethod) {
      previousStatusRef.current = status;
      return;
    }

    const wasErrorStatus = previousStatusRef.current === 'error';
    previousStatusRef.current = status;

    if (wasErrorStatus && status === '') {
      autoProcessCalledRef.current = false;
      setIsAutoProcessPending(true);
    }

    if (autoProcessCalledRef.current) {
      return;
    }

    autoProcessCalledRef.current = true;
    setIsAutoProcessPending(false);
    void processPaymentDirectly(autoProcessPaymentMethod);
  }, [autoProcessPaymentMethod, processPaymentDirectly, status]);

  const goToDepositLimitsStep = () => {
    setStatus('deposit-limits');
    skipToStep('deposit-limits');
  };

  const handleCancelNewLimit = useCallback(() => {
    if (!pendingDepositLimit || !pendingDepositLimit.pendingLimit) return;
    setIsLoading(true);

    const period = pendingDepositLimit.period;
    const periodTranslation = t(`deposit_limits.${getTranslationKeyForPeriod(period)}`).toLowerCase();
    const currentLimit = depositLimits?.limits?.find(limit => 'period' in limit && limit.period === period);
    const formattedAmount = formatter((currentLimit && 'limitValue' in currentLimit ? currentLimit.limitValue : 0) || 0)
      .format(nf.WITHOUT_DECIMALS)
      .get();
    const formattedDeclinedAmount = formatter(pendingDepositLimit.pendingLimit.value || 0)
      .format(nf.WITHOUT_DECIMALS)
      .get();

    declineLimit[period].mutate(undefined, {
      onSuccess: () => {
        toast(
          t('deposit_limits.declined_toast', {
            period: periodTranslation,
            amount: formattedAmount,
          }),
          { type: 'info' }
        );
        setIsLoading(false);
      },
      onError: () => {
        toast(
          t('deposit_limits.declined_error_toast', {
            period: periodTranslation,
            amount: formattedDeclinedAmount,
          }),
          { type: 'error' }
        );
        setIsLoading(false);
      },
    });
  }, [pendingDepositLimit]);

  const handleApplyNewLimit = useCallback(() => {
    if (!pendingDepositLimit || !pendingDepositLimit.pendingLimit) return;
    setIsLoading(true);

    const period = pendingDepositLimit.period;
    const periodTranslation = t(`deposit_limits.${getTranslationKeyForPeriod(period)}`).toLowerCase();
    const formattedAcceptedAmount = formatter(pendingDepositLimit.pendingLimit.value).format(nf.WITHOUT_DECIMALS).get();

    acceptLimit[period].mutate(undefined, {
      onSuccess: () => {
        toast(
          t('deposit_limits.accepted_toast', {
            period: periodTranslation,
            amount: formattedAcceptedAmount,
          }),
          { type: 'info' }
        );
        setIsLoading(false);
      },
      onError: () => {
        toast(
          t('deposit_limits.accepted_error_toast', {
            period: periodTranslation,
            amount: formattedAcceptedAmount,
          }),
          { type: 'error' }
        );
        setIsLoading(false);
      },
    });
  }, [pendingDepositLimit]);

  useEffect(() => {
    if (!depositLimitsProp?.limits) {
      setPendingDepositLimit(null);
      return;
    }

    setPendingDepositLimit(findPendingRaiseLimit(depositLimitsProp.limits));
  }, [depositLimitsProp]);

  useEffect(() => {
    if (!pendingDepositLimit) {
      setPendingLimitActions([]);
      return;
    }
    const actions = [
      {
        label: t('deposit_limits.cancel_new_limit'),
        onClick: handleCancelNewLimit,
        'data-track-name': 'Cancel',
      },
    ];
    if (pendingDepositLimit?.pendingLimit?.validFrom && new Date(pendingDepositLimit.pendingLimit!.validFrom).getTime() < Date.now()) {
      actions.push({
        label: t('deposit_limits.apply_new_limit'),
        onClick: handleApplyNewLimit,
        'data-track-name': 'Apply',
      });
    }
    setPendingLimitActions(actions);
  }, [pendingDepositLimit]);

  const handlePaymentMethodChange = useCallback(
    (methodOrInstrument: MethodOrInstrument) => {
      selectedPaymentMethodOrInstrumentRef.current = methodOrInstrument;
      setSelectedPaymentMethodOrInstrument(methodOrInstrument);
      const identifier = methodOrInstrument.apiReference ?? methodOrInstrument.name;
      setValue('amountStep.paymentMethod', identifier, { shouldValidate: false });
    },
    [setValue]
  );

  const isAutoProcessing = isAutoProcessPending || (!!autoProcessPaymentMethod && isLoading);

  return (
    <div className="space-y-8" data-testid="deposit-amount-step" {...props}>
      {netDepositLimit && Number(netDepositLimit.remainingAmount || 0) <= 0 ? (
        <StatusNotification
          label={t('deposit_limits.net_deposit_limit_message_fully_reached_title')}
          description={t('deposit_limits.net_deposit_limit_message_fully_reached_description', {
            date: new Date(netDepositLimit.expirationTime).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
            time: new Date(netDepositLimit.expirationTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          })}
          type="warning"
          variant="fixed"
          dataTrackType="Spend Limit"
          dataTrackMessage="Limit Reached"
        />
      ) : (
        <>
          {pendingDepositLimit && (
            <StatusNotification
              label={t('deposit_limits.pending_limit_message_title', { period: t(`deposit_limits.period_${pendingDepositLimit.period.toLowerCase()}`) })}
              description={t('deposit_limits.pending_limit_message_description', {
                date: new Date(pendingDepositLimit.pendingLimit!.creationTime).toLocaleDateString(),
                hour: new Date(pendingDepositLimit.pendingLimit!.creationTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                amount: formatter(pendingDepositLimit.pendingLimit!.value).format(nf.WITH_DECIMALS).get(),
                period: t(`deposit_limits.period_${pendingDepositLimit.period.toLowerCase()}`),
              })}
              type="info"
              variant="floating"
              dataTrackType="Deposit Limit"
              dataTrackMessage="Limit Increase"
              data-track-period={pendingDepositLimit.period === 'DAY' ? 'daily' : pendingDepositLimit.period === 'WEEK' ? 'weekly' : 'monthly'}
              actions={pendingLimitActions}
            />
          )}
          <div className="flex flex-col gap-2">
            <PaymentMethodSelector namespace={namespace} disabled={isLoading || isAutoProcessing} value={paymentMethod} onChange={handlePaymentMethodChange} />
            <FieldMessage id="paymentMethod-error" message={errors.amountStep?.paymentMethod?.message as string} messageType="error" />
          </div>
          <FormField
            name="amountStep.amount"
            render={({ field, fieldState }) => (
              <TextInput
                {...field}
                id="amountStep.amount"
                className="peer/amount"
                disabled={isLoading || isAutoProcessing}
                type="number"
                min="0"
                step="0.01"
                leftIcon={tGeneral('currency_symbol')}
                label={t('amount.label')}
                placeholder={t('amount.placeholder')}
                helpMessage={fieldState.error?.message}
                helpMessageType={fieldState.error ? 'error' : undefined}
                data-testid="amount-input"
                onChange={e => {
                  const value = e.target.value;
                  if (value === '' || (!isNaN(Number(value)) && Number(value) >= 0)) {
                    field.onChange(e);
                  }
                }}
                onWheel={e => {
                  e.currentTarget.blur();
                }}
                inputTrackingProps={{
                  'data-track-name': 'Amount',
                }}
                helperTrackingProps={{
                  'data-track-helper': 'Amount',
                }}
              />
            )}
          />

          <div className="flex flex-row gap-3" data-track-container="Quick Amounts">
            {quickAmounts.map((quickAmount, index) => {
              const value = String(quickAmount / 100);
              return (
                <Button
                  key={quickAmount}
                  type="button"
                  variant={amount === value ? 'primary' : 'secondary'}
                  className="flex-1 rounded-md"
                  onClick={() => {
                    setValue('amountStep.amount', value);
                  }}
                  disabled={isDepositLimitsLoading || isLoading || isApplePayLoading || isAutoProcessing}
                  data-testid={`quick-amount-${value}-button`}
                  data-track-name="Quick Amount"
                  data-track-value={value}
                  data-track-position={index + 1}
                >
                  {formatter(quickAmount).format(nf.WITHOUT_DECIMALS).get()}
                </Button>
              );
            })}
          </div>

          {showMinimumAmountNotMetWarning && (
            <StatusNotification
              className="peer-focus-within/amount:hidden"
              type="warning"
              label={t('amount.minimum_amount_not_met_title')}
              description={t('amount.minimum_amount_not_met_description', {
                amount: formatter(minimumAmountForBonus ?? 0)
                  .format(NumberFormats.WITHOUT_DECIMALS)
                  .get(),
              })}
            />
          )}

          {showTermsCheckbox && (
            <FormField
              name="amountStep.termsAccepted"
              render={({ field, fieldState }) => (
                <Checkbox
                  {...field}
                  id="amountStep.termsAccepted"
                  disabled={isLoading || isAutoProcessing}
                  checked={field.value}
                  onCheckedChange={checked => {
                    field.onChange(checked);
                  }}
                  data-testid="terms-checkbox"
                  label={
                    <p className="paragraph-s">
                      <span>{t('terms.text_1')}</span>
                      <Link href={ROUTES.FOOTER.LEGAL.TERMS} target="_blank" rel="noopener" className="!paragraph-s-strong" data-track-name="Terms and Conditions">
                        {t('terms.link_1')}
                      </Link>
                      <span>{t('terms.text_2')}</span>
                      <Link href={ROUTES.FOOTER.LEGAL.GAMBLING_COMMISSION} target="_blank" rel="noopener" className="!paragraph-s-strong" data-track-name="UK Gambling Commission">
                        {t('terms.link_2')}
                      </Link>
                      <span>{t('terms.text_3')}</span>
                      <Link href={ROUTES.FOOTER.LEGAL.DOCUMENTS} target="_blank" rel="noopener" className="!paragraph-s-strong" data-track-name="Documents Required">
                        {t('terms.link_3')}
                      </Link>
                      <span>{t('terms.text_4')}</span>
                    </p>
                  }
                  helpMessage={fieldState.error?.message}
                  helpMessageType={fieldState.error ? 'error' : undefined}
                  alignment="start"
                  trackingProps={{
                    'data-track-name': 'Agree',
                  }}
                />
              )}
            />
          )}

          {amountExceedsNetDepositLimit && (
            <StatusNotification
              label={t('deposit_limits.net_deposit_limit_message_partially_reached_title')}
              description={t('deposit_limits.net_deposit_limit_message_partially_reached_description', {
                amount: formatter(netDepositLimit?.remainingAmount || 0)
                  .format(nf.WITH_DECIMALS)
                  .get(),
              })}
              type="warning"
              variant="fixed"
              dataTrackType="Spend Limit"
              dataTrackMessage="Amount Exceeded"
            />
          )}

          <div className="mt-auto md:mt-0 flex flex-col gap-8" data-track-container="Actions">
            <Button
              type="button"
              className={`w-full${paymentMethod === DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name ? ` ${APPLE_PAY_BUTTON_CLASS_NAME}` : ''}`}
              onClick={handleConfirm}
              data-track-name="Continue"
              data-track-method={selectedPaymentMethodOrInstrument?.name || paymentMethod}
              data-track-type="FTD"
              data-track-count="1"
              data-track-amount={amount}
              disabled={
                isDepositLimitsLoading || isLoading || isApplePayLoading || isPaymentsLimitsLoading || amountExceedsNetDepositLimit || !isPaymentsPlayersFetched || isAutoProcessing
              }
              data-testid="next-button"
            >
              {isDepositLimitsLoading || isLoading || isApplePayLoading || !isPaymentsPlayersFetched || isAutoProcessing ? (
                <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} />
              ) : paymentMethod === DEPOSIT_PAYMENT_METHODS.APPLE_PAY.name ? (
                <>
                  <span>{t('apple_pay_button.pay_with')}</span>
                  <AppStore size="small" className={APPLE_PAY_ICON_CLASS_NAME} />
                  <span>{t('apple_pay_button.pay')}</span>
                </>
              ) : (
                t('next_button')
              )}
            </Button>

            {showDepositLimitButtonOnDepositAmountStep ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={goToDepositLimitsStep}
                data-track-name="Deposit Limits"
                disabled={isDepositLimitsLoading || isAutoProcessing}
                data-testid="deposit-limit-button"
              >
                {isDepositLimitsLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={t('aria.loading')} /> : t('deposit_limits.button')}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

export { DepositAmountStep };
